import type { SectionSummary } from '@privisy/api-types';
import type { ComplianceCheck, SectionName } from './types';
import { fixGroupOf } from '../components/report/fixGroups';

/**
 * Copy for the sales report (the page a prospect opens from an outreach
 * email). Everything here is derived from the redacted SalesReportSummary,
 * which carries section aggregates and highlights but never a check name, so
 * no sentence may claim more than those aggregates show.
 */

/** Customer-facing area names; the report's section titles are internal jargon ("GPC Signal Validation"). */
const AREA_LABEL: Record<SectionName, string> = {
  privacyPolicy: 'Privacy policy',
  gpcValidation: 'Opt-out signal',
  uiCompliance: 'Consent and privacy links',
  trackerDetection: 'Third-party trackers',
  cipa: 'CIPA exposure',
};

/** The same areas as a noun modifier: "failed 3 of 6 opt-out signal checks". */
const AREA_NOUN: Record<SectionName, string> = {
  privacyPolicy: 'privacy policy',
  gpcValidation: 'opt-out signal',
  uiCompliance: 'consent and privacy link',
  trackerDetection: 'tracker',
  cipa: 'CIPA',
};

const items = (n: number) => `${n} ${n === 1 ? 'item' : 'items'}`;
const checksWord = (n: number) => (n === 1 ? 'check' : 'checks');

/**
 * Checks that measured a failure. The backend's failedChecks also counts
 * checks that errored, which measured nothing (#1123, #1160), so a claim
 * sent to a prospect subtracts them.
 */
function measuredFailures(s: SectionSummary): number {
  return s.failedChecks - (s.errorChecks ?? 0);
}

/**
 * The status this page shows (#1896). The backend's section 'warning' means
 * "needs a second look", which covers a HIGH/MEDIUM/LOW check that failed
 * (determineSectionStatus) as well as an advisory warning that
 * warningChecks already leaves out (#1132). Read as-is, a section whose only
 * finding is a HIGH failure was badged REVIEW and headlined "0 items to
 * review". So: any measured failure is a failure, and a warning with
 * nothing failed and nothing to review (advisory only) is a pass. A warning
 * whose only failures errored stays a warning: it measured nothing, and
 * claiming either a pass or a failure would overstate it.
 */
type SalesStatus = SectionSummary['status'];
function salesStatus(s: SectionSummary): SalesStatus {
  if (s.status !== 'warning') return s.status;
  if (measuredFailures(s) > 0) return 'non_compliant';
  if (s.warningChecks === 0 && s.failedChecks === 0) return 'compliant';
  return 'warning';
}

// Which failing area leads, before any count. Failing to honor an opt-out
// preference signal (§ 1798.135(b), § 7025) is the violation behind the AG's
// Sephora and Honda actions and the most concrete, testable finding the
// scanner has, so it leads. Consent and privacy links come next (the visible
// "Do Not Sell or Share" duty), then the policy. Ranking by raw failed-check
// count instead would let the policy section, which has several times as
// many checks as GPC, outrank it almost every time. CIPA never reaches this page
// (SALES_SECTION_ORDER); it is listed only to complete the record.
const LEAD_PRIORITY: Record<SectionName, number> = {
  gpcValidation: 0,
  uiCompliance: 1,
  privacyPolicy: 2,
  trackerDetection: 3,
  cipa: 9,
};

// 0: failed. 1: needs review and was actually scored. An unscored warning
// (an inconclusive GPC run) measured nothing, so it can never lead the page.
function leadRank(s: SectionSummary): number {
  const status = salesStatus(s);
  if (status === 'non_compliant') return 0;
  if (status === 'warning' && s.score !== null) return 1;
  return 2;
}

/**
 * The one area the page leads with: failed before scored-warning, then by
 * LEAD_PRIORITY (enforcement weight, see above), then most measured failures,
 * then lowest score.
 */
export function pickLeadSection(sections: SectionSummary[]): SectionSummary | null {
  const candidates = sections
    .filter((s) => leadRank(s) < 2)
    .sort((a, b) =>
      leadRank(a) - leadRank(b)
      || LEAD_PRIORITY[a.sectionKey] - LEAD_PRIORITY[b.sectionKey]
      || measuredFailures(b) - measuredFailures(a)
      || (a.score ?? 101) - (b.score ?? 101)
      || b.warningChecks - a.warningChecks);
  return candidates[0] ?? null;
}

/**
 * A headline in three parts, so the page can set the middle one in the
 * color of its tone. It counts failed areas when more than one failed
 * (#2131), otherwise it speaks for the lead section. The tone is the one
 * that section's table row carries, so "No failed checks" is never set in
 * the failure color.
 */
export interface SalesHeadline {
  lead: string;
  emphasis: string;
  tail: string;
  tone: RowTone;
}

export function salesHeadline(domain: string, sections: SectionSummary[]): SalesHeadline {
  const lead = pickLeadSection(sections);
  if (!lead) {
    return sections.some((s) => s.status === 'compliant' || s.status === 'warning')
      ? { lead: '', emphasis: 'No failed checks', tail: ` on ${domain}.`, tone: 'pass' }
      : { lead: 'We could not complete the checks on ', emphasis: domain, tail: '.', tone: 'neutral' };
  }
  // #2131: with more than one area failing, one section's count undersells
  // the table right below it, so lead with the count of failed areas. A
  // failed area is one salesRow badges FAIL, and the denominator is the rows
  // the table shows (a NOT RUN row included), so neither number is invented.
  const shown = sections.filter((s) => s.sectionKey !== 'cipa');
  const failing = shown.filter((s) => salesStatus(s) === 'non_compliant').length;
  if (failing > 1) {
    const emphasis = failing === shown.length ? `all ${shown.length}` : `${failing} of the ${shown.length}`;
    return { lead: `${domain} failed `, emphasis, tail: ' CCPA areas we check.', tone: 'fail' };
  }
  const noun = AREA_NOUN[lead.sectionKey];
  // A warning lead with nothing to review failed only on errored checks, so
  // it falls through to "could not be completed", never "0 items" (#1896).
  if (salesStatus(lead) === 'warning' && lead.warningChecks > 0) {
    return { lead: `${domain} has `, emphasis: `${items(lead.warningChecks)} to review`, tail: ` in its ${noun} checks.`, tone: 'review' };
  }
  if (lead.sectionKey === 'privacyPolicy') {
    // #1164: the count spans conditional disclosures too, so it is "the N we
    // check for", never "the N CCPA requires". Only when something is
    // missing: the section can fail on checks outside the count (OOPS
    // disclosure, request mechanism, right to limit), and "14 of the 14" in
    // the failure color would hide that gap.
    if (lead.policyDisclosures && lead.policyDisclosures.present < lead.policyDisclosures.total) {
      const { present, total } = lead.policyDisclosures;
      return { lead: 'Your privacy policy includes ', emphasis: `${present} of the ${total}`, tail: ' CCPA disclosures we check for.', tone: 'fail' };
    }
    if (lead.highlights.includes('Privacy policy not found')) {
      return { lead: 'We ', emphasis: 'could not find a privacy policy', tail: ` on ${domain}.`, tone: 'fail' };
    }
  }
  const failed = measuredFailures(lead);
  if (failed === 0) {
    return { lead: '', emphasis: `${lead.failedChecks} of ${lead.totalChecks}`, tail: ` ${noun} checks on ${domain} could not be completed.`, tone: 'neutral' };
  }
  return { lead: `${domain} failed `, emphasis: `${failed} of ${lead.totalChecks}`, tail: ` ${noun} checks.`, tone: 'fail' };
}

/**
 * Nothing failed and nothing to review, so the owner's close and unlock offer
 * sell the record of what passed rather than fixes. The "No failed checks"
 * headline alone is not enough (#2149): an unscored warning section (an
 * unsettled GPC run) can never lead it, yet its warning is a finding with a
 * fix. CIPA is outside every CCPA count, so its warnings do not count here.
 * A Confirm warning is not a finding (#1653), so only Fix and Recommended
 * checks in `findings` do.
 */
export function isCleanReport(sections: SectionSummary[], findings?: ComplianceCheck[]): boolean {
  if (salesHeadline('', sections).tone !== 'pass') return false;
  if (sections.some((s) => s.sectionKey !== 'cipa' && s.warningChecks > 0)) return false;
  return !(findings ?? []).some((c) => {
    const group = fixGroupOf(c);
    return group === 'fix' || group === 'recommended';
  });
}

export type RowTone = 'fail' | 'review' | 'pass' | 'neutral';

export interface SalesRow {
  area: string;
  result: string;
  badge: 'FAIL' | 'REVIEW' | 'PASS' | 'NO TEST' | 'NOT RUN';
  tone: RowTone;
}

export function salesRow(s: SectionSummary): SalesRow {
  const area = AREA_LABEL[s.sectionKey];
  // #1123: a section that never ran shows no score and no result claim.
  if (s.status === 'skipped' || s.status === 'error') {
    return { area, result: s.status === 'skipped' ? 'Not run in this scan' : 'Could not be completed', badge: 'NOT RUN', tone: 'neutral' };
  }

  const failed = measuredFailures(s);
  const errored = s.errorChecks ?? 0;
  let result: string;
  if (s.policyDisclosures) {
    const { present, total } = s.policyDisclosures;
    result = `${present} of ${total} disclosures found`;
    // Every disclosure present yet FAIL: say what else failed, or the badge
    // reads as a scanner error.
    if (present === total && failed > 0) {
      result += `, ${failed} other ${checksWord(failed)} failed`;
    }
  } else if (s.highlights.length > 0) {
    result = s.highlights.join(', ');
  } else if (s.failedChecks > 0) {
    result = failed > 0 ? `${failed} of ${s.totalChecks} checks failed` : `${errored} of ${s.totalChecks} checks could not be completed`;
    if (failed > 0 && errored > 0) result += `, ${errored} could not be completed`;
  } else if (s.warningChecks > 0) {
    result = `${items(s.warningChecks)} to review`;
  } else {
    result = `All ${s.totalChecks} checks passed`;
  }

  const status = salesStatus(s);
  if (status === 'non_compliant') return { area, result, badge: 'FAIL', tone: 'fail' };
  if (status === 'warning') {
    return s.score === null
      ? { area, result, badge: 'NO TEST', tone: 'neutral' }
      : { area, result, badge: 'REVIEW', tone: 'review' };
  }
  return { area, result, badge: 'PASS', tone: 'pass' };
}

/** The grade the outreach email quotes, so the page never contradicts it. */
export function overallGrade(score: number | null, letterGrade: string | null): string {
  if (score === null || !letterGrade) return 'Not scored';
  return `${letterGrade}, ${Math.round(score)} of 100`;
}
